import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Eye, RefreshCw, UserCheck, UserX } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Approuvés' },
  { value: 'rejected', label: 'Rejetés' },
];

const normalizeStatus = (value) => String(value || '').toLowerCase();

const getStatusBadge = (statusValue) => {
  const status = normalizeStatus(statusValue);
  if (status === 'approved') {
    return <Badge className="bg-green-100 text-green-700">Approuvé</Badge>;
  }
  if (status === 'rejected') {
    return <Badge className="bg-red-100 text-red-700">Rejeté</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const AmbassadorApplicationsManagement = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [applications, setApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [decisionDialog, setDecisionDialog] = useState({
    open: false,
    action: 'approve',
    item: null,
    note: '',
  });

  const loadApplications = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await axios.get(`${API}/admin/ambassador-applications`, {
          params: {
            status: statusFilter,
          },
        });
        setApplications(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Impossible de charger les candidatures AMBASSADEUR.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const openDecisionDialog = (action, item) => {
    setDecisionDialog({
      open: true,
      action,
      item,
      note: '',
    });
  };

  const closeDecisionDialog = () => {
    setDecisionDialog({
      open: false,
      action: 'approve',
      item: null,
      note: '',
    });
  };

  const closeDetailsDialog = () => {
    setDetailsItem(null);
  };

  const submitDecision = async () => {
    const item = decisionDialog.item;
    if (!item?.id) {
      return;
    }
    const action = decisionDialog.action === 'reject' ? 'reject' : 'approve';

    setProcessingId(item.id);
    try {
      await axios.post(`${API}/admin/ambassador-applications/${item.id}/${action}`, {
        note: decisionDialog.note?.trim() || null,
      });
      toast.success(action === 'approve' ? 'Candidature approuvée.' : 'Candidature rejetée.');
      closeDecisionDialog();
      loadApplications(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de traiter la candidature.");
    } finally {
      setProcessingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return applications;
    return applications.filter((item) => {
      const haystack = [
        item.full_name,
        item.email,
        item.phone,
        item.country,
        item.city,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(query);
    });
  }, [applications, search]);

  const summary = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter((item) => normalizeStatus(item.status) === 'pending').length;
    const approved = applications.filter((item) => normalizeStatus(item.status) === 'approved').length;
    const rejected = applications.filter((item) => normalizeStatus(item.status) === 'rejected').length;
    const priority = applications.filter((item) => Boolean(item.meets_priority_audience)).length;
    return { total, pending, approved, rejected, priority };
  }, [applications]);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold">Candidatures AMBASSADEUR</h2>
            <p className="text-sm text-gray-500">
              Validation admin des profils externes AMBASSADEUR avant activation de compte.
            </p>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => loadApplications(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex justify-end">
          <Button variant="outline" className="gap-2" onClick={() => loadApplications(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total</CardDescription>
            <CardTitle>{summary.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>En attente</CardDescription>
            <CardTitle>{summary.pending}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Approuvés</CardDescription>
            <CardTitle>{summary.approved}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Rejetés</CardDescription>
            <CardTitle>{summary.rejected}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Audience 10k+</CardDescription>
            <CardTitle>{summary.priority}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="ambassador-status-filter">Statut</Label>
            <select
              id="ambassador-status-filter"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="ambassador-search">Recherche</Label>
            <Input
              id="ambassador-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nom, email, telephone, ville..."
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Liste des candidatures</CardTitle>
          <CardDescription>{filteredItems.length} candidature(s) visible(s).</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-sm text-gray-500">Chargement des candidatures...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidat</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Réseaux</TableHead>
                    <TableHead>Soumise le</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => {
                    const status = normalizeStatus(item.status);
                    const canReview = status === 'pending';
                    const links = Array.isArray(item.social_links) ? item.social_links : [];
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium">{item.full_name || '-'}</div>
                          <div className="text-xs text-gray-500">
                            {item.country || '-'} {item.city ? `- ${item.city}` : ''}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{item.email || '-'}</div>
                          <div className="text-xs text-gray-500">{item.phone || '-'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-semibold">{Number(item.audience_size || 0).toLocaleString('fr-FR')}</div>
                          <Badge variant={item.meets_priority_audience ? 'default' : 'outline'} className="mt-1">
                            {item.meets_priority_audience ? '10k+ prioritaire' : 'À vérifier'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[260px]">
                          {links.length === 0 ? (
                            <span className="text-gray-400">-</span>
                          ) : (
                            <div className="space-y-1">
                              {links.slice(0, 2).map((link) => (
                                <a
                                  key={`${item.id}-${link}`}
                                  href={link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-cyan-700 hover:underline block truncate"
                                >
                                  {link}
                                </a>
                              ))}
                              {links.length > 2 && (
                                <div className="text-xs text-gray-500">+{links.length - 2} lien(s)</div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(item.submitted_at)}</TableCell>
                        <TableCell>{getStatusBadge(status)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDetailsItem(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Détail
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDecisionDialog('approve', item)}
                              disabled={!canReview || processingId === item.id}
                            >
                              <UserCheck className="w-4 h-4 mr-1" />
                              Approuver
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openDecisionDialog('reject', item)}
                              disabled={!canReview || processingId === item.id}
                            >
                              <UserX className="w-4 h-4 mr-1" />
                              Rejeter
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {filteredItems.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">Aucune candidature pour les filtres actuels.</div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(detailsItem)} onOpenChange={(open) => (!open ? closeDetailsDialog() : undefined)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détail candidature AMBASSADEUR</DialogTitle>
            <DialogDescription>
              {detailsItem?.full_name || '-'} ({detailsItem?.email || '-'})
            </DialogDescription>
          </DialogHeader>

          {detailsItem && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div>
                  <p className="font-medium text-gray-900">Contact</p>
                  <p className="text-gray-600">{detailsItem.phone || '-'}</p>
                  <p className="text-gray-600">
                    {detailsItem.country || '-'} {detailsItem.city ? `- ${detailsItem.city}` : ''}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Audience déclarée</p>
                  <p className="text-gray-600">
                    {Number(detailsItem.audience_size || 0).toLocaleString('fr-FR')} abonnés
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Statut</p>
                  <div className="mt-1">{getStatusBadge(detailsItem.status)}</div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Soumise le</p>
                  <p className="text-gray-600">{formatDateTime(detailsItem.submitted_at)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="font-medium text-gray-900">Réseaux sociaux</p>
                  <div className="mt-1 space-y-1">
                    {(detailsItem.social_links || []).length === 0 ? (
                      <p className="text-gray-500">Aucun lien fourni.</p>
                    ) : (
                      (detailsItem.social_links || []).map((link) => (
                        <a
                          key={`${detailsItem.id}-${link}`}
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-cyan-700 hover:underline block break-all"
                        >
                          {link}
                        </a>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Message</p>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {detailsItem.message || 'Aucun message fourni.'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Note de revue</p>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {detailsItem.review_note || 'Aucune note admin.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDetailsDialog}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={decisionDialog.open} onOpenChange={(open) => (!open ? closeDecisionDialog() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionDialog.action === 'approve' ? 'Approuver la candidature' : 'Rejeter la candidature'}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog.item?.full_name || '-'} ({decisionDialog.item?.email || '-'})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              {decisionDialog.action === 'approve'
                ? "L'approbation active un compte AMBASSADEUR et un profil d'affiliation dédié."
                : 'Le rejet conserve la candidature avec statut rejected.'}
            </div>
            <div>
              <Label htmlFor="ambassador-review-note">Note interne (optionnelle)</Label>
              <Textarea
                id="ambassador-review-note"
                value={decisionDialog.note}
                onChange={(event) => setDecisionDialog((current) => ({ ...current, note: event.target.value }))}
                placeholder="Commentaire de revue admin"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDecisionDialog}>
              Annuler
            </Button>
            <Button
              onClick={submitDecision}
              disabled={processingId === decisionDialog.item?.id}
              variant={decisionDialog.action === 'approve' ? 'default' : 'destructive'}
            >
              {processingId === decisionDialog.item?.id
                ? 'Traitement...'
                : decisionDialog.action === 'approve'
                  ? 'Confirmer approbation'
                  : 'Confirmer rejet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-4 text-sm text-green-900 flex gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Une candidature approuvée reçoit un compte AMBASSADEUR, un code affilié et l’accès au tableau de bord affiliation.
        </CardContent>
      </Card>
    </div>
  );
};

export default AmbassadorApplicationsManagement;
